import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contractorProfileApi } from './api';

// --- Profile Hooks ---
export function useContractorProfile() {
    return useQuery({
        queryKey: ['contractorProfile'],
        queryFn: contractorProfileApi.getProfile,
        staleTime: 1000 * 60 * 5,
        retry: 1, // Don't endlessly retry if not found (404 is expected for new orgs)
    });
}

export function useUpdateContractorProfile() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: contractorProfileApi.updateProfile,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['contractorProfile'] });
        },
    });
}

// --- Key Personnel Hooks ---
export function useKeyPersonnel() {
    return useQuery({
        queryKey: ['keyPersonnel'],
        queryFn: contractorProfileApi.getPersonnel,
        staleTime: 1000 * 60 * 5,
    });
}

export function useCreateKeyPersonnel() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: contractorProfileApi.createPersonnel,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['keyPersonnel'] });
        },
    });
}

export function useUpdateKeyPersonnel() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: any }) => contractorProfileApi.updatePersonnel(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['keyPersonnel'] });
        },
    });
}

export function useDeleteKeyPersonnel() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: contractorProfileApi.deletePersonnel,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['keyPersonnel'] });
        },
    });
}

// --- Past Performance Hooks ---
export function usePastPerformances() {
    return useQuery({
        queryKey: ['pastPerformances'],
        queryFn: contractorProfileApi.getPastPerformance,
        staleTime: 1000 * 60 * 5,
    });
}

export function useCreatePastPerformance() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: contractorProfileApi.createPastPerformance,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['pastPerformances'] });
        },
    });
}

export function useUpdatePastPerformance() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: any }) => contractorProfileApi.updatePastPerformance(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['pastPerformances'] });
        },
    });
}

export function useDeletePastPerformance() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: contractorProfileApi.deletePastPerformance,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['pastPerformances'] });
        },
    });
}

// --- Certifications Hooks ---
export function useCertifications() {
    return useQuery({
        queryKey: ['certifications'],
        queryFn: contractorProfileApi.getCertifications,
        staleTime: 1000 * 60 * 5,
    });
}

export function useCreateCertification() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: contractorProfileApi.createCertification,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['certifications'] });
        },
    });
}

export function useUpdateCertification() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: any }) => contractorProfileApi.updateCertification(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['certifications'] });
        },
    });
}

export function useDeleteCertification() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: contractorProfileApi.deleteCertification,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['certifications'] });
        },
    });
}

// --- Insurance Hooks ---
export function useInsurance() {
    return useQuery({
        queryKey: ['insuranceProfiles'],
        queryFn: contractorProfileApi.getInsurance,
        staleTime: 1000 * 60 * 5,
    });
}

export function useCreateInsurance() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: contractorProfileApi.createInsurance,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['insuranceProfiles'] });
        },
    });
}

export function useUpdateInsurance() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: any }) => contractorProfileApi.updateInsurance(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['insuranceProfiles'] });
        },
    });
}

export function useDeleteInsurance() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: contractorProfileApi.deleteInsurance,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['insuranceProfiles'] });
        },
    });
}
